import { CalendarIcon } from '@heroicons/react/24/outline';
import { type InputHTMLAttributes, forwardRef, useId } from 'react';

import { cn } from '../utils/cn';

export interface DatePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  /**
   * Label for the date picker
   */
  label?: string;

  /**
   * Helper text to display below the input
   */
  helperText?: string;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Size of the date picker
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether the field is required
   * @default false
   */
  required?: boolean;

  /**
   * Custom class for the container
   */
  containerClassName?: string;

  /**
   * Custom class for the label
   */
  labelClassName?: string;

  /**
   * Show calendar icon
   * @default true
   */
  showIcon?: boolean;
}

/**
 * DatePicker component
 *
 * A date input component with label, helper text, and error state support.
 * Uses native HTML5 date input with enhanced styling.
 *
 * @example
 * ```tsx
 * <DatePicker
 *   label="Start Date"
 *   helperText="Select the project start date"
 * />
 *
 * <DatePicker
 *   label="Due Date"
 *   error="This field is required"
 *   required
 * />
 * ```
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      label,
      helperText,
      error,
      size = 'md',
      required = false,
      disabled,
      className,
      containerClassName,
      labelClassName,
      showIcon = true,
      id,
      ...props
    },
    ref
  ) => {
    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-3 text-lg',
    };

    const labelSizeStyles = {
      sm: 'text-sm',
      md: 'text-sm',
      lg: 'text-sm',
    };

    const helperSizeStyles = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };

    const iconSizeStyles = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    const baseStyles = [
      'block w-full rounded-lg border-2 bg-white dark:bg-gray-800',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
      'disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed disabled:text-gray-500 dark:disabled:text-gray-600',
    ];

    const stateStyles = error
      ? [
          'border-error-500 text-error-900 dark:text-error-200',
          'focus:border-error-500 focus:ring-error-500',
        ]
      : [
          'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100',
          'focus:border-primary-500 focus:ring-primary-500',
          'hover:border-gray-400 dark:hover:border-gray-500',
        ];

    const autoId = useId();
    const generatedId = id || `datepicker-${autoId}`;

    return (
      <div className={cn('w-full', containerClassName)}>
        {label && (
          <label
            htmlFor={generatedId}
            className={cn(
              'block font-medium mb-1',
              labelSizeStyles[size],
              disabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300',
              error && 'text-error-700 dark:text-error-400',
              labelClassName
            )}
          >
            {label}
            {required && <span className="text-error-500 dark:text-error-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <input
            ref={ref}
            type="date"
            id={generatedId}
            disabled={disabled}
            required={required}
            className={cn(
              baseStyles,
              stateStyles,
              sizeStyles[size],
              showIcon && 'pr-10',
              className
            )}
            {...props}
          />
          {showIcon && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <CalendarIcon
                className={cn(
                  iconSizeStyles[size],
                  disabled
                    ? 'text-gray-400 dark:text-gray-600'
                    : 'text-gray-400 dark:text-gray-500',
                  error && 'text-error-500 dark:text-error-400'
                )}
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        {helperText && !error && (
          <p
            className={cn(
              'mt-1.5 text-gray-500 dark:text-gray-400',
              helperSizeStyles[size],
              disabled && 'text-gray-400 dark:text-gray-600'
            )}
          >
            {helperText}
          </p>
        )}

        {error && (
          <p className={cn('mt-1.5 text-error-600 dark:text-error-400', helperSizeStyles[size])}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';
